import { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'
import styled from '@emotion/styled'
import SvgGauge from 'svg-gauge'
import { modulosActions, getModulosComEstatisticas } from '../modulos/ModulosStore'
import { progressoActions } from '../conteudo/ProgressoReduxStore'
import { Button, Card, TituloPagina } from '../components'
import { formatDate } from '../common/Utils'
import { getGaugeColorByPercent } from './utils/gaugeColor'
import { SimuladosStore, TentativaSimulado } from './SimuladosStore'

interface SimuladosParams {
  cursoId: string
}

type TentativaMeta =
  | { prefixo: 'Módulo:' | 'Módulos:'; valor: string; texto?: never }
  | { texto: 'Simulado Geral'; prefixo?: never; valor?: never }
  | null

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

const gaugeDefaultOptions = {
  animDuration: 1,
  showValue: false,
  dialStartAngle: 160,
  dialEndAngle: 20,
  initialValue: 0,
  max: 100,
}

export const Simulados = () => {
  return (
    <Simulados.Layout>
      <InitSimuladosData />
      <SimuladosHeader />
      <SimuladosActions />
      <TentativasSection />
    </Simulados.Layout>
  )
}

function TentativaGauge({ value, total, percentual }) {
  const { Layout, Percent, GaugeTotal, GaugeContainer } = TentativaGauge
  const gaugeEl = useRef<HTMLDivElement | null>(null)
  const gaugeRef = useRef<any>(null)
  const gaugeMaxRef = useRef<number | null>(null)
  const safeTotal = Math.max(total || 0, 1)
  const safeValue = clamp(value || 0, 0, safeTotal)
  const safePercent = clamp(percentual || 0, 0, 100)
  const gaugeColor = getGaugeColorByPercent(safePercent)

  useEffect(() => {
    if (!gaugeEl.current) return
    if (!gaugeRef.current || gaugeMaxRef.current !== safeTotal) {
      gaugeEl.current.innerHTML = ''
      const options = { ...gaugeDefaultOptions, max: safeTotal }
      gaugeRef.current = SvgGauge(gaugeEl.current, options)
      gaugeRef.current.setValue(options.initialValue)
      gaugeMaxRef.current = safeTotal
    }
    gaugeRef.current.setValueAnimated(safeValue, 1)
  }, [safeValue, safeTotal])

  return (
    <Layout>
      <Percent>{safePercent}%</Percent>
      <GaugeTotal>
        <strong>
          {safeValue}/{safeTotal}
        </strong>{' '}
        acertos
      </GaugeTotal>
      <GaugeContainer color={gaugeColor}>
        <div ref={gaugeEl} className="gauge-container" />
      </GaugeContainer>
    </Layout>
  )
}

function getTentativaMeta({
  modulosTentativa,
  cursoFinalizado,
}: {
  modulosTentativa: Array<{ id: number; nome: string }> | undefined
  cursoFinalizado: boolean
}): TentativaMeta {
  if (!modulosTentativa?.length) return null
  if (modulosTentativa.length === 1) {
    return { prefixo: 'Módulo:', valor: modulosTentativa[0].nome }
  }
  if (cursoFinalizado) {
    return { texto: 'Simulado Geral' }
  }
  return {
    prefixo: 'Módulos:',
    valor: modulosTentativa.map((modulo) => modulo.nome).join(', '),
  }
}

function getSimuladosDerived(modulos: any[]) {
  const moduloAtualEmAndamento = modulos.find((modulo) => {
    const percentual = modulo.estatisticaEstudo?.percentualProgressoMaisAvancado ?? 0
    return percentual > 0 && !modulo.estatisticaEstudo?.moduloCompleto
  })

  const modulosComConteudoEstudado = modulos.filter((modulo) => {
    const percentual = modulo.estatisticaEstudo?.percentualProgressoMaisAvancado ?? 0
    return percentual > 0
  })

  const cursoFinalizado = modulos.length
    ? modulos.every((modulo) => !!modulo.estatisticaEstudo?.moduloCompleto)
    : false

  const isCursoModuloUnico = modulos.length === 1
  const temConteudoEstudado = modulosComConteudoEstudado.length > 0
  const mostrarBotaoModuloAtual =
    !isCursoModuloUnico && !cursoFinalizado && !!moduloAtualEmAndamento
  const mostrarBotaoSimuladoGeral =
    !isCursoModuloUnico &&
    temConteudoEstudado &&
    (modulosComConteudoEstudado.length > 1 || !mostrarBotaoModuloAtual)
  const labelBotaoSimuladoGeral =
    !cursoFinalizado && modulosComConteudoEstudado.length === 1 && !mostrarBotaoModuloAtual
      ? 'Fazer simulado'
      : cursoFinalizado
        ? 'Fazer simulado geral'
        : 'Fazer simulado geral (módulos estudados)'
  const mostrarFallbackSemBotoes =
    !isCursoModuloUnico && !mostrarBotaoSimuladoGeral && !mostrarBotaoModuloAtual

  return {
    cursoFinalizado,
    isCursoModuloUnico,
    temConteudoEstudado,
    mostrarBotaoModuloAtual,
    mostrarBotaoSimuladoGeral,
    labelBotaoSimuladoGeral,
    mostrarFallbackSemBotoes,
    moduloAtualId: moduloAtualEmAndamento?.id ?? null,
  }
}

function InitSimuladosData() {
  const reduxDispatch = useDispatch()
  const simuladosDispatch = SimuladosStore.useDispatch()
  const { cursoId } = useParams<SimuladosParams>()
  const modulos = useSelector(getModulosComEstatisticas)
  const hasModulos = modulos.length > 0
  const hasEstatisticas = modulos.some(
    (modulo) => modulo.estatisticaEstudo?.percentualProgressoMaisAvancado != null
  )

  useEffect(() => {
    if (!cursoId) return
    if (!hasModulos) {
      reduxDispatch(modulosActions.loadModulos(+cursoId))
    }
    if (!hasEstatisticas) {
      reduxDispatch(progressoActions.loadEstatisticasEstudo(+cursoId))
    }
    simuladosDispatch(SimuladosStore.thunks.loadTentativas())
  }, [cursoId, reduxDispatch, simuladosDispatch, hasModulos, hasEstatisticas])
  return null
}

function useSimuladosDerived() {
  const modulos = useSelector(getModulosComEstatisticas)
  return useMemo(() => getSimuladosDerived(modulos), [modulos])
}

function useTentativasState() {
  const { tentativas, tentativasStatus } = SimuladosStore.useState()
  return {
    tentativas,
    tentativasLoading: tentativasStatus.loading,
    tentativasError: tentativasStatus.error,
  }
}

function SimuladosHeader() {
  return (
    <Simulados.Header>
      <TituloPagina>Simulados</TituloPagina>
    </Simulados.Header>
  )
}

function SimuladosActions() {
  const derived = useSimuladosDerived()
  const history = useHistory()
  const { cursoId } = useParams<SimuladosParams>()

  function irParaRegrasConteudoAcumulado() {
    history.push(`/curso/${cursoId}/regras-simulado?tipo=conteudo-acumulado`)
  }

  function irParaRegrasModuloAtual() {
    if (!derived.moduloAtualId) return
    history.push(
      `/curso/${cursoId}/regras-simulado?tipo=modulo-atual&moduloId=${derived.moduloAtualId}`
    )
  }

  return (
    <Simulados.Actions>
      {derived.isCursoModuloUnico ? (
        <Button
          label="Fazer simulado"
          onClick={irParaRegrasConteudoAcumulado}
          disabled={!derived.temConteudoEstudado}
        />
      ) : null}
      {!derived.isCursoModuloUnico && derived.mostrarBotaoSimuladoGeral ? (
        <Button
          label={derived.labelBotaoSimuladoGeral}
          onClick={irParaRegrasConteudoAcumulado}
          disabled={!derived.temConteudoEstudado}
        />
      ) : null}
      {derived.mostrarBotaoModuloAtual ? (
        <Button label="Fazer simulado do módulo atual" onClick={irParaRegrasModuloAtual} />
      ) : null}
      {derived.mostrarFallbackSemBotoes ? (
        <Button
          label="Fazer simulado"
          onClick={irParaRegrasConteudoAcumulado}
          disabled={!derived.temConteudoEstudado}
        />
      ) : null}
    </Simulados.Actions>
  )
}

function TentativaCardItem({
  tentativa,
  cursoFinalizado,
}: {
  tentativa: TentativaSimulado
  cursoFinalizado: boolean
}) {
  const history = useHistory()
  const { cursoId } = useParams<SimuladosParams>()
  const percentual =
    tentativa.qtdQuestoes > 0
      ? Math.round((tentativa.qtdQuestoesCorretas / tentativa.qtdQuestoes) * 100)
      : 0
  const tentativaMeta = getTentativaMeta({
    modulosTentativa: tentativa.modulos,
    cursoFinalizado,
  })

  function verGabarito(avaliacaoId: number) {
    history.push(`/curso/${cursoId}/simulados/gabarito/${avaliacaoId}`)
  }

  return (
    <Simulados.TentativaCard key={tentativa.id}>
      <Simulados.TentativaInfo>
        <div>{formatDate(tentativa.createdAt, 'dd/MM/yyyy HH:mm')}</div>
        {tentativaMeta ? (
          <Simulados.TentativaMeta>
            {tentativaMeta.texto ? (
              <strong>{tentativaMeta.texto}</strong>
            ) : (
              <>
                {tentativaMeta.prefixo} <strong>{tentativaMeta.valor}</strong>
              </>
            )}
          </Simulados.TentativaMeta>
        ) : null}
      </Simulados.TentativaInfo>
      <Simulados.TentativaAcoes>
        <TentativaGauge
          value={tentativa.qtdQuestoesCorretas}
          total={tentativa.qtdQuestoes}
          percentual={percentual}
        />
      </Simulados.TentativaAcoes>
      <Simulados.BotaoGabarito>
        <Button label="Ver gabarito" onClick={() => verGabarito(tentativa.id)} />
      </Simulados.BotaoGabarito>
    </Simulados.TentativaCard>
  )
}

function TentativasSection() {
  const { tentativas, tentativasLoading, tentativasError } = useTentativasState()
  const { cursoFinalizado } = useSimuladosDerived()

  return (
    <>
      <Simulados.Divider />

      <TituloPagina>Tentativas</TituloPagina>

      {tentativasError ? <Simulados.ErrorState>{tentativasError}</Simulados.ErrorState> : null}

      {tentativasLoading ? (
        <Simulados.LoadingState>Carregando tentativas...</Simulados.LoadingState>
      ) : null}

      {!tentativasLoading && !tentativas.length ? (
        <Simulados.EmptyState>
          <strong>Você ainda não possui tentativas finalizadas de simulado.</strong>
        </Simulados.EmptyState>
      ) : null}

      <Simulados.TentativasLista>
        {tentativas.map((tentativa) => (
          <TentativaCardItem
            key={tentativa.id}
            tentativa={tentativa}
            cursoFinalizado={cursoFinalizado}
          />
        ))}
      </Simulados.TentativasLista>
    </>
  )
}

Simulados.Layout = styled.div`
  display: grid;
  gap: 18px;
  @media (max-width: 768px) {
    margin: 0 12px;
  }
`

Simulados.Header = styled.div``

Simulados.Actions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  button {
    width: auto;
    height: 46px;
    padding: 14px;
  }

  @media (max-width: 620px) {
    display: grid;
    gap: 8px;

    button {
      width: 100%;
      padding: 0;
    }
  }
`

Simulados.Divider = styled.div`
  border-top: 1px solid #d9d9d9;
  margin-top: 11px;
  padding-top: 11px;
`

Simulados.ErrorState = styled.div`
  color: #c43f2f;
  font-size: 14px;
`

Simulados.LoadingState = styled.div`
  color: #606060;
  font-size: 14px;
`

Simulados.EmptyState = styled.div`
  width: 100%;
  height: 120px;
  display: grid;
  place-items: center;
  font-size: 14px;
  line-height: 18px;
  color: #2d2d2d;
  text-align: center;
`

Simulados.TentativasLista = styled.div`
  display: grid;
  gap: 16px;
`

Simulados.TentativaCard = styled(Card)`
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  padding: 18px;
  gap: 24px;

  @media (max-width: 620px) {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      'info gauge'
      'button button';
    align-items: start;
    column-gap: 12px;
    row-gap: 14px;
  }
`

Simulados.TentativaInfo = styled.div`
  display: grid;
  gap: 6px;
  color: #2d2d2d;

  @media (max-width: 620px) {
    grid-area: info;
    min-width: 0;
  }
`

Simulados.TentativaAcoes = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 620px) {
    grid-area: gauge;
    align-self: start;
  }
`

Simulados.BotaoGabarito = styled.div`
  @media (max-width: 620px) {
    grid-area: button;
    width: 100%;

    button {
      width: 100%;
    }
  }
`

Simulados.TentativaMeta = styled.div`
  font-size: 14px;
  white-space: normal;
  word-break: break-word;
`

TentativaGauge.Layout = styled.div`
  position: relative;
  width: 140px;
  min-width: 140px;
  height: 92px;

  @media (max-width: 620px) {
    width: 96px;
    min-width: 96px;
    height: 64px;
  }
`

TentativaGauge.Percent = styled.div`
  position: absolute;
  top: 40px;
  width: 100%;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: #2d2d2d;

  @media (max-width: 620px) {
    top: 28px;
    font-size: 13px;
    line-height: 14px;
  }
`

TentativaGauge.GaugeTotal = styled.div`
  position: absolute;
  top: 62px;
  width: 100%;
  text-align: center;
  font-size: 12px;
  line-height: 13px;
  color: #2d2d2d;

  @media (max-width: 620px) {
    top: 42px;
    font-size: 9px;
    line-height: 10px;
  }
`

TentativaGauge.GaugeContainer = styled.div<{ color: string }>`
  .gauge-container {
    width: 140px;
    height: 92px;

    @media (max-width: 620px) {
      width: 96px;
      height: 64px;
    }
  }

  .gauge-container > .gauge .value {
    stroke: ${(props) => props.color};
    stroke-width: 6;
    fill: rgba(0, 0, 0, 0);
  }

  .gauge-container > .gauge .dial {
    stroke: #e7e7e7;
    stroke-width: 6;
    fill: rgba(0, 0, 0, 0);
  }

  .gauge-container > .gauge path {
    stroke-linecap: round;
  }
`
